import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react'

function cx(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(' ')
}

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger'

export function Button({
  variant = 'primary',
  size,
  block,
  className,
  type = 'button',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant
  size?: 'sm'
  block?: boolean
}) {
  return (
    <button
      type={type}
      className={cx(
        'btn',
        `btn-${variant}`,
        size === 'sm' && 'btn-sm',
        block && 'btn-block',
        className,
      )}
      {...rest}
    />
  )
}

type FieldProps = {
  label?: ReactNode
  hint?: ReactNode
  error?: ReactNode
  required?: boolean
  htmlFor?: string
  children: ReactNode
}

export function FormField({
  label,
  hint,
  error,
  required,
  htmlFor,
  children,
}: FieldProps) {
  return (
    <div className="form-field">
      {label && (
        <label htmlFor={htmlFor}>
          {label}
          {required && <span className="required">*</span>}
        </label>
      )}
      {children}
      {error ? (
        <span className="error">{error}</span>
      ) : hint ? (
        <span className="hint">{hint}</span>
      ) : null}
    </div>
  )
}

export function Input({
  className,
  ...rest
}: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cx('input', className)} {...rest} />
}

export function Select({
  className,
  children,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={cx('select', className)} {...rest}>
      {children}
    </select>
  )
}

export function Textarea({
  className,
  ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={cx('textarea', className)} {...rest} />
}

type CardProps = {
  title?: ReactNode
  actions?: ReactNode
  footer?: ReactNode
  children?: ReactNode
  className?: string
  bodyClassName?: string
}

export function Card({
  title,
  actions,
  footer,
  children,
  className,
  bodyClassName,
}: CardProps) {
  return (
    <section className={cx('card', className)}>
      {(title || actions) && (
        <header className="card-header">
          {typeof title === 'string' ? <h2>{title}</h2> : title}
          {actions && <div className="row-end">{actions}</div>}
        </header>
      )}
      <div className={cx('card-body', bodyClassName)}>{children}</div>
      {footer && <div className="card-footer">{footer}</div>}
    </section>
  )
}

type AlertProps = {
  variant?: 'info' | 'success' | 'warning' | 'danger'
  children: ReactNode
}

export function Alert({ variant = 'info', children }: AlertProps) {
  return <div className={cx('alert', `alert-${variant}`)}>{children}</div>
}

type BadgeProps = {
  variant?: 'default' | 'success' | 'warning' | 'danger' | 'muted'
  children: ReactNode
}

export function Badge({ variant = 'default', children }: BadgeProps) {
  const klass = variant === 'default' ? '' : `badge-${variant}`
  return <span className={cx('badge', klass)}>{children}</span>
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: ReactNode
  description?: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="empty">
      <h3>{title}</h3>
      {description && <p className="muted">{description}</p>}
      {action && <div style={{ marginTop: 16 }}>{action}</div>}
    </div>
  )
}
